import React, { useRef, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation, Outlet, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import PasswordReset from './pages/PasswordReset';
import Logout from './pages/Logout';
import Register from './pages/Register';
import Header from './components/Header';
import Footer from './components/Footer';
import CsvUploader from './components/CSVUploader';
import ProductPage from './pages/ProductPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider, useCart } from './context/cartContext';
import { CustomerProvider } from './context/customerContext';

import Cart from './pages/Cart';
import FloatingCart from './components/FloatingCart';
import OrderEdit from './pages/OrderEdit';
import OrderDetails from './pages/OrderDetails';
import OrderHistory from './pages/OrderHistory';
import { ToastContainer } from 'react-toastify';

import { ConfigProvider } from './context/ConfigContext';
import CustomerManagement from './pages/CustomerManagement';
import { LocationProvider } from './context/LocationContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import InvoiceDetails from './pages/InvoiceDetails';
import InvoiceHistory from './pages/InvoiceHistory';
import StockPage from './pages/Stock';
import QrGenerator from './pages/QrGenerator';
import QrScanner from './pages/QrScanner';
import ScanHistoryDetails from './components/ScanHistoryDetails';
import ProtectedRoute from './components/ProtectedRoute';

import GpsTracker from './components/GpsTracker';
import { StockLocationProvider } from './context/StockLocationContext';
import DashBoard from './pages/MyInvois/DashBoard';

import TaskReminder from './pages/TaskReminder/TaskReminder';
import AppSettings from './pages/AppSettings';

//abb 260824
import { getLicense } from './utils/api';

const googleClientId = window.APP_CONFIG?.REACT_APP_GOOGLE_CLIENT_ID;

// Function to determine basename dynamically
const getDynamicBasename = (): string => {
  const currentPathname = window.location.pathname;
  if (currentPathname.startsWith('/shop/') || currentPathname === '/shop') {
    return '/shop';
  }
  return '/';
};

// 仅针对 vektron.u8.my 的根路由重定向逻辑
const RootRedirect = () => {
  const { user } = useAuth();
  const isVektron = window.location.hostname === 'vektron.u8.my';

  if (isVektron) {
    if (user) {
      // vektron 已登录：跳转到库存管理
      return <Navigate to="/stock-management" replace />;
    } else {
      // vektron 未登录：跳转到登录页
      return <Navigate to="/login" replace />;
    }
  }

  // 其他域名保持原样显示 Home 页面
  return <Home />;
};

const App = () => {
  const cartRef = useRef<HTMLDivElement>(null);
  const dynamicBasename = getDynamicBasename();

  const [currentDomain] = useState<string>(window.location.hostname);

  // Instant Session Caching for License Check (0ms on reload if already checked)
  const cachedLicenseValid = sessionStorage.getItem(`u8my_license_valid_${currentDomain}`) === 'true';

  const [isLicenseExpired, setIsLicenseExpired] = useState<boolean>(false);
  const [checkingLicense, setCheckingLicense] = useState<boolean>(!cachedLicenseValid);
  const [license, setLicense] = useState<any | null>(() => {
    const cachedInfo = sessionStorage.getItem(`u8my_license_info_${currentDomain}`);
    return cachedInfo ? JSON.parse(cachedInfo) : null;
  });

  useEffect(() => {
    // If already verified valid in sessionStorage, skip network delay
    if (cachedLicenseValid) {
      return;
    }

    const checkLicenseValidity = async () => {
      if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
        setCheckingLicense(false);
        return;
      }
      try {
        const lic = await getLicense(currentDomain);
        setLicense(lic);
        if (lic && lic.ends) {
          const [day, month, year] = lic.ends.split('-');
          const endsDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          endsDate.setHours(0, 0, 0, 0);

          if (today > endsDate) {
            setIsLicenseExpired(true);
          } else {
            // Cache successful valid license in sessionStorage for instant subsequent loads
            sessionStorage.setItem(`u8my_license_valid_${currentDomain}`, 'true');
            sessionStorage.setItem(`u8my_license_info_${currentDomain}`, JSON.stringify(lic));
          }
        }
      } catch (err) {
        console.error('License check error:', err);
      } finally {
        setCheckingLicense(false);
      }
    };
    checkLicenseValidity();
  }, [currentDomain, cachedLicenseValid]);

  // 1. Show loading state only on very first session load
  if (checkingLicense) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-gray-500 font-medium animate-pulse">Checking u8.my license status...</p>
      </div>
    );
  }

  // 2. Block application access if the license is expired
  if (isLicenseExpired) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 p-6 text-center">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg border border-red-200">
          <h1 className="text-2xl font-bold text-red-600 mb-4">License Expired</h1>
          <p className="text-gray-700 mb-6">
            The license for <strong className="text-gray-900">{currentDomain}</strong> is expired on <strong className="text-gray-900">{license?.ends}</strong>.
          </p>
          <p className="text-sm text-gray-500">
            Please contact support
            <a href="mailto:boyd.chung@gmail.com" className="font-semibold text-red-600"> boyd.chung@gmail.com </a> /
            <a href="tel:+601112163808" className="font-semibold text-red-600"> +601112163808 </a>
            , or renew your license to restore access to this application.
          </p>
        </div>
      </div>
    );
  }

  if (!googleClientId) {
    console.error("FATAL ERROR: Google Client ID is not defined. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.");
    return (
      <div style={{ color: 'red', textAlign: 'center', marginTop: '20px', padding: '20px', border: '1px solid red' }}>
        Application configuration error: Google Client ID is missing.
        <br />
        Google Login cannot be initialized. Please check the console for more details.
      </div>
    );
  }

  return (
    <ConfigProvider>
      <HelmetProvider>
        <GoogleOAuthProvider clientId={googleClientId}>
          <BrowserRouter basename={dynamicBasename} future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <AuthProvider>
              <CartProvider>
                <CustomerProvider>
                  <LocationProvider>
                    <StockLocationProvider>

                      <div className="app-most-top flex flex-col min-h-dvh min-w-full">
                        <HeaderWrapper />
                        <main className="flex-grow">
                          <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password/:token" element={<PasswordReset />} />

                            <Route element={window.location.hostname === 'vektron.u8.my' ? <ProtectedRoute /> : <Outlet />}>
                              <Route path="/" element={<RootRedirect />} />
                              <Route path="/product/:productId/:productName?" element={<ProductPage />} />
                              <Route path="/logout" element={<Logout />} />
                              <Route path="/csv-uploader" element={<CsvUploader />} />
                              <Route path="/cart" element={<Cart />} />
                              <Route path="/new-order" element={<OrderEdit />} />
                              <Route path="/new-order/draft/:orderId" element={<OrderEdit />} />
                              <Route path="/order-history" element={<OrderHistory />} />
                              <Route path="/order/:orderId" element={<OrderDetails />} />
                              <Route path="/invoice-history" element={<InvoiceHistory />} />
                              <Route path="/invoices/:invoice_id" element={<InvoiceDetails />} />
                              <Route path="/customer-management" element={<CustomerManagement />} />
                              <Route path="/stock-management" element={<StockPage />} />
                              <Route path="/qr-generator" element={<QrGenerator />} />
                              <Route path="/qr-scanner" element={<QrScanner />} />
                              <Route path="/scan-history/:movement_type/:id" element={<ScanHistoryDetails />} />
                              <Route path="/task-reminder" element={<TaskReminder />} />
                              <Route path="/myinvois/dashboard" element={<DashBoard />} />
                              <Route path="/gps-tracker" element={<GpsTracker />} />
                              <Route path="/app-settings" element={<AppSettings />} />
                            </Route>

                            <Route path="*" element={<div className="text-center mt-20"><a href='/'>Page Not Found. Back to Home</a></div>} />
                          </Routes>
                        </main>
                        <FloatingCart cartRef={cartRef} />
                        <Footer />
                        <ToastContainer
                          position="top-center"
                          autoClose={5000}
                          hideProgressBar={false}
                          newestOnTop={false}
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss
                          draggable
                          pauseOnHover
                          theme="light"
                        />
                      </div>

                    </StockLocationProvider>
                  </LocationProvider>
                </CustomerProvider>
              </CartProvider>
            </AuthProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </HelmetProvider>
    </ConfigProvider>
  );
};

const HeaderWrapper = () => {
  const location = useLocation();
  const shouldHideHeader = location.pathname === '/';
  return shouldHideHeader ? null : <Header />;
};

export default App;
